import { createContext, useContext, useState, useEffect, useRef } from 'react';
import API from '../utils/api';

const AuthContext = createContext(null);

/**
 * Convert any error coming back from the API into a clean Bengali message.
 * Backend already returns human-readable strings in `message`; we just
 * catch a few common shapes in case of network/CORS failures.
 */
export const authErrorMessage = (err) => {
  // axios error with response from server
  if (err?.response?.data?.message) return err.response.data.message;
  // 401 (should rarely hit here because login uses its own try/catch) — same wording
  if (err?.response?.status === 401) return 'ভুল ইমেইল/ফোন বা পাসওয়ার্ড।';
  if (err?.response?.status === 403) return 'এই অ্যাকশনের অনুমতি নেই।';
  if (err?.response?.status === 404) return 'প্রয়োজনীয় তথ্য পাওয়া যায়নি।';
  if (err?.response?.status === 429) return 'অনেক চেষ্টা হয়েছে — একটু পরে আবার চেষ্টা করুন।';
  if (err?.response?.status >= 500) return 'সার্ভারে সমস্যা হচ্ছে — কিছুক্ষণ পরে আবার চেষ্টা করুন।';
  // Network / CORS / no-response
  if (err?.message === 'Network Error') return 'নেটওয়ার্ক সমস্যা — ইন্টারনেট কানেকশন চেক করুন।';
  if (err?.code === 'ECONNABORTED') return 'সার্ভার সাড়া দিচ্ছে না — কিছুক্ষণ পরে আবার চেষ্টা করুন।';
  // Fallback
  return 'কিছু একটা সমস্যা হয়েছে — আবার চেষ্টা করুন।';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bypass axios's global 401→page-redirect for our own login/register calls
  // (axios interceptor sees ANY 401 as a "session expired" event by default
  // and force-reloads to /login, which kicks the user out mid-login).
  // Login payloads opt-out via this request-scope flag.
  const inFlightAuthCall = useRef(false);

  const installBypass = () => {
    inFlightAuthCall.current = true;
    return () => { inFlightAuthCall.current = false; };
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try { setUser(JSON.parse(savedUser)); }
      catch { localStorage.removeItem('token'); localStorage.removeItem('user'); }
    }
    setLoading(false);
  }, []);

  /**
   * Login. Accepts either an email OR a phone in `identifier`.
   * Always uses raw fetch() for the auth endpoint so we don't trigger
   * axios's "401 = reload page" interceptor (which would hide our error
   * message and yank the user back to a fresh /login).
   */
  const login = async (identifier, password) => {
    // Prefer the same-origin API base the rest of the app uses
    const base = window.location.origin + '/api';
    const res = await fetch(`${base}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Throw an axios-compatible error shape so callers can read .response.data.message
      const err = new Error('Login failed');
      err.response = { status: res.status, data };
      throw err;
    }
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const register = async (name, email, phone, password) => {
    const base = window.location.origin + '/api';
    const res = await fetch(`${base}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, phone, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error('Registration failed');
      err.response = { status: res.status, data };
      throw err;
    }
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{
      user, login, register, logout, updateUser, loading,
      // exposed for callers (other components) if they want to opt out of
      // the global 401→reload interceptor on their own API calls too
      _inFlightAuthCall: inFlightAuthCall,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
